import React from "react";
import styled from "styled-components";

import talkImage from "assets/talk.svg";
import { floatAnimation } from "components/styles/animations";
import { getNPCData } from "libs/stats";
import useDialog from "components/hooks/useDialog";
import Dialogs from "./Dialogs";
import Tooltip, { TooltipDesc, TooltipName } from "./Tooltip";
import { media } from "components/styles/breakpoints";
import { getFightStatus } from "redux/selectors/fightSelectors";
import { useSelector } from "react-redux";
import { isFightModeStatus } from "libs/fight";

interface NPCProps {
  slug: string;
}

interface NPCWrapperProps {
  position: number;
  heightPercentage: number;
  isFaded: boolean;
}

const NPCWrapper = styled.div<NPCWrapperProps>`
  position: absolute;
  left: ${(props) => props.position * 0.2}%;
  height: ${(props) => props.heightPercentage}%;
  z-index: 2;
  animation: ${floatAnimation} 3s ease-in infinite;
  cursor: pointer;
  pointer-events: auto;
  ${(props) => props.isFaded && "opacity: 0.2;"}

  &:hover {
    filter: saturate(120%) brightness(110%);
  }
`;

const NPCImage = styled.img`
  height: 100%;
`;

const InfoWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: absolute;
  bottom: 80%;
  text-shadow: 0 0 3px #000000;
`;

const Name = styled.div`
  color: #95ebf5;
  font-size: 14px;
  text-align: center;
  font-weight: 400;
  display: flex;
  align-items: center;
  margin-bottom: 2px;

  ${media.desktop`
    font-size: 18px;
    margin-bottom: 5px;
  `}
`;

const TalkIcon = styled.div`
  width: 20px;
  height: 20px;
  margin-left: 6px;
  filter: drop-shadow(0 0 3px #00000099);
  mask: url(${talkImage}) no-repeat center;
  background-color: #95ebf5;
`;

const DialogWrapper = styled.div`
  width: clamp(200px, 30dvw, 400px);
`;

export default function NPC({ slug }: NPCProps) {
  const npc = getNPCData(slug);
  const { currentStep, currentDialog, hasAction, onNextStep } = useDialog(
    npc.dialog,
    npc.isDialogOpen
  );

  const status = useSelector(getFightStatus);
  const isFightMode = isFightModeStatus(status);

  return (
    <NPCWrapper
      position={npc.position}
      heightPercentage={npc.heightPercentage}
      isFaded={isFightMode}
      onClick={onNextStep}
    >
      <InfoWrapper>
        <Tooltip
          color="#95ebf5"
          content={
            <>
              <TooltipName>{npc.name}</TooltipName>
              <TooltipDesc>{`${npc.name} is a (probably) friendly NPC that would like to chat with you.`}</TooltipDesc>
            </>
          }
        >
          <Name>
            {npc.name}
            <TalkIcon />
          </Name>
        </Tooltip>
        <DialogWrapper>
          <Dialogs
            avatar={npc.avatar}
            currentStep={currentStep}
            currentDialog={currentDialog}
            hasAction={hasAction}
          />
        </DialogWrapper>
      </InfoWrapper>
      <NPCImage alt={npc.name} src={npc.image} />
    </NPCWrapper>
  );
}
